import { WooCommerceRestaurantHolidays } from '../../core/interfaces/woo-commerce/api/restaurant/restaurant-api.interface';

/**
 * Convert a Date to the holiday format string "dd-mm-yyyy"
 * Note: The holidays object uses "dd-mm-yyyy" format (e.g., "24-12-2025")
 */
export function dateToHolidayFormat(date: Date): string {
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
}

/**
 * Get a date that is N days from the given date
 */
export function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

/**
 * Check if a given date is a holiday
 * @param date The date to check
 * @param holidays The holidays object from the API
 * @param timezone Optional timezone to use for date calculation
 * @returns true if the date is a holiday, false otherwise
 */
export function isDateHoliday(
  date: Date,
  holidays: WooCommerceRestaurantHolidays | undefined,
  timezone?: string
): boolean {
  if (!holidays || Object.keys(holidays).length === 0) {
    return false;
  }

  // If timezone is provided, convert the date to that timezone
  let dateToCheck = date;
  if (timezone) {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    const parts = formatter.formatToParts(date);
    const month = parseInt(parts.find((p) => p.type === 'month')?.value || '1', 10);
    const day = parseInt(parts.find((p) => p.type === 'day')?.value || '1', 10);
    const year = parseInt(parts.find((p) => p.type === 'year')?.value || '2000', 10);
    dateToCheck = new Date(year, month - 1, day);
  }

  const dateKey = dateToHolidayFormat(dateToCheck);
  return dateKey in holidays;
}

/**
 * Check if today is a holiday
 * @param holidays The holidays object from the API
 * @param timezone Optional timezone to use for date calculation
 * @returns true if today is a holiday, false otherwise
 */
export function isTodayHoliday(
  holidays: WooCommerceRestaurantHolidays | undefined,
  timezone?: string
): boolean {
  return isDateHoliday(new Date(), holidays, timezone);
}

/**
 * Filter out holiday dates from an array of available dates
 * @param availableDates Array of dates to filter
 * @param holidays The holidays object from the API
 * @param timezone Optional timezone to use for date calculation
 * @returns Array of dates that are not holidays
 */
export function filterOutHolidays(
  availableDates: Date[],
  holidays: WooCommerceRestaurantHolidays | undefined,
  timezone?: string
): Date[] {
  if (!holidays || Object.keys(holidays).length === 0) {
    return availableDates;
  }

  return availableDates.filter((date) => !isDateHoliday(date, holidays, timezone));
}

/**
 * Get holiday name/reason for a specific date
 * @param date The date to check
 * @param holidays The holidays object from the API
 * @param timezone Optional timezone to use for date calculation
 * @returns Array of holiday names or undefined if not a holiday
 */
export function getHolidayNames(
  date: Date,
  holidays: WooCommerceRestaurantHolidays | undefined,
  timezone?: string
): string[] | undefined {
  if (!holidays || Object.keys(holidays).length === 0) {
    return undefined;
  }

  // If timezone is provided, convert the date to that timezone
  let dateToCheck = date;
  if (timezone) {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    const parts = formatter.formatToParts(date);
    const month = parseInt(parts.find((p) => p.type === 'month')?.value || '1', 10);
    const day = parseInt(parts.find((p) => p.type === 'day')?.value || '1', 10);
    const year = parseInt(parts.find((p) => p.type === 'year')?.value || '2000', 10);
    dateToCheck = new Date(year, month - 1, day);
  }

  const dateKey = dateToHolidayFormat(dateToCheck);
  return holidays[dateKey];
}

